import CheckmarkIcon from 'assets/svg/checkmark-circle-brand.svg';
import Modal from 'ds/components/modal';
import { H3, Text } from 'ds/components/typography';
import React, { useEffect, useState } from 'react';
import {
  SavedSuccessBlock,
  Content,
  FiltersWrapper,
  FrequencyBlock,
  FrequencyList,
} from './styled';
import { useLocale } from 'locale';
import { Button } from 'ds/components/button';
import { UpdateFrequency } from '../config';
import FilterThumbnails from 'components/filters/thumbnails';
import { IFiltersState } from 'components/filters/types';
import RadioGroup from 'ds/components/input/RadioGroup';
import { ISaveSearch, IUpdateSaveSearch } from 'store/sagas/apiService/types';
import { connect } from 'react-redux';
import { updateSaveSearchFrequency } from 'store/state/app/actions';
import { latestSavedSearchFiltersSelector, latestSavedSearchSelector } from 'store/state/selectors/savedSearchPage';
import { State } from 'store/state';
import { getMultiLocationTitle } from '../shared/formatters';
import { useSendEvent } from 'analytics/useSendEvent';

interface PostSavedModalProps {
  filters: Partial<IFiltersState>;
  savedSearch?: ISaveSearch;
  isDesktop?: boolean;
  onSubscriptionChange: (search: IUpdateSaveSearch) => void;
  onClose: () => void;
}

export const frequencies: UpdateFrequency[] = [
  UpdateFrequency.Realtime,
  UpdateFrequency.Daily,
  UpdateFrequency.Weekly,
  UpdateFrequency.Never,
];

const PostSavedModal: React.FC<PostSavedModalProps> = ({
  onClose,
  filters,
  onSubscriptionChange,
  savedSearch,
  isDesktop,
}) => {
  const { t } = useLocale();
  const [ frequency, setFrequency ] = useState<UpdateFrequency>(UpdateFrequency.Daily);
  const sendEvent = useSendEvent();

  useEffect(() => {
    if (savedSearch) {
      setFrequency(savedSearch.updateFrequency);
    }
  }, [ savedSearch ]);

  useEffect(() => {
    sendEvent('search_save_search_form_open', 'search', {
      event: {
        source: 'market_place',
      },
    });
  }, []);

  const multiLocationTitle = getMultiLocationTitle(savedSearch, t);

  const frequencyOptions = frequencies.map(value => ({
    label: t('postSavedModal.frequency', { frequency: value }),
    value,
  }));

  const handleFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as UpdateFrequency;
    setFrequency(value);
    sendEvent('search_save_search_email_frequency_click', 'search', {
      event: {
        source: 'saved_search',
        frequency_type: value,
      },
    });
  };

  const handleSubmit = () => {
    sendEvent('search_save_search_form_submit', 'search', {
      event: {
        source: 'market_place',
        frequency_type: frequency,
      },
    });

    if (frequency === savedSearch.updateFrequency) {
      onClose();
      return;
    }

    onSubscriptionChange({
      searchId: savedSearch.searchId,
      updateFrequency: frequency,
      term: savedSearch.term,
    });
    onClose();
  };

  return (
    <Modal
      title={t('postSavedModal.title')}
      // fixing bad title on mobile when hideCloseButton=true
      titleStyle={isDesktop ? {} : {
        fontWeight: 'bold',
        fontSize: '17px',
      }}
      desktopModalPositionCenter={isDesktop}
      mobileModalPositionBottom={!isDesktop}
      contentWrapperStyle={isDesktop ? { width: 500 } : {
        paddingTop: 16,
      }}
      isOpen
      hideCloseButton
      onClose={onClose}
    >
      <SavedSuccessBlock>
        <CheckmarkIcon />
        <Text>{t('postSavedModal.saved')}</Text>
      </SavedSuccessBlock>
      <Content>
        <FiltersWrapper>
          <H3 weight="bold">{multiLocationTitle}</H3>
          <FilterThumbnails showDealType filters={filters} />
        </FiltersWrapper>
        <FrequencyBlock>
          <H3 weight="bold">{t('postSavedModal.frequencyTitle')}</H3>
          <FrequencyList>
            <RadioGroup
              options={frequencyOptions}
              value={frequency}
              name="frequency"
              onChange={handleFrequencyChange}
            />
          </FrequencyList>
        </FrequencyBlock>
        <Button size="extraLarge" onClick={handleSubmit}>{t('postSavedModal.submit')}</Button>
      </Content>
    </Modal>
  );
};

const mapStateToProps = (state: State) => ({
  savedSearch: latestSavedSearchSelector(state),
  filters: latestSavedSearchFiltersSelector(state),
});

const mapDispatchToProps = {
  onSubscriptionChange: updateSaveSearchFrequency,
};

export default connect(mapStateToProps, mapDispatchToProps)(PostSavedModal);
